<template>
    <div ref="root" class="ai-follow-root">
        <v-tooltip v-if="visible" bottom max-width="340" open-delay="150">
            <template v-slot:activator="{ on, attrs }">
                <div ref="chip" class="ai-follow-bar" :class="'ai-follow-bar--' + mode" role="status" aria-live="polite" v-bind="attrs" v-on="on">
                    <v-progress-circular v-if="working" indeterminate size="14" width="2" color="primary" class="mr-2" />
                    <v-icon v-else small :color="mode === 'paused' ? 'orange darken-2' : 'primary'" class="mr-1">{{ chipIcon }}</v-icon>
                    <span class="ai-follow-text">{{ chipText }}</span>
                    <span v-if="unread" class="ai-follow-dot error" aria-label="New answer"></span>
                    <v-btn x-small text color="primary" class="ml-1" @click.stop="togglePanel">{{ panelOpen ? "Hide chat" : "Chat" }}</v-btn>
                    <v-btn v-if="!working" icon x-small aria-label="End the preview mode" @click.stop="dismiss"><v-icon x-small>mdi-close</v-icon></v-btn>
                </div>
            </template>
            <span>{{ tooltipText }}</span>
        </v-tooltip>
        <AiCursor ref="cursor" />
        <div v-show="visible && panelOpen" ref="panel" class="ai-follow-panel-wrap" :style="panelStyle">
            <AiFollowPanel v-if="visible && panelOpen" :loading="busy" :label="label" @close="panelOpen = false" @full="openChat" />
        </div>
    </div>
</template>

<script>
    import AiCursor from "./AiCursor.vue"
    import AiFollowPanel from "./AiFollowPanel.vue"
    import { resolveFollowTarget, followStepLabel, followWanted } from "./aiCursorMap"

    const CHAT_PATH = "/seo-ai"
    // Eine Analyse ruft dasselbe Tool fuer 25 Projekte auf: ein Seitenwechsel braucht mindestens so lange Abstand, und es zaehlt nur der
    // neueste Schritt - sonst laedt der Cursor eine schwere Projektseite nach der anderen.
    const MIN_DWELL_MS = 4000
    const ELEMENT_WAIT_MS = 3000
    // Reiter und Kachel einer Projektseite erscheinen erst, wenn deren Daten geladen sind (mehrere Sekunden).
    const PAGE_WAIT_MS = 12000
    const TOOLTIPS = {
        following:
            "Quaro AI is showing you where the data lives: the cursor opens the project, the tab and the tile. Click anywhere on the page to take over - the preview then stops on purpose, so it never moves your screen while you work. The answer keeps coming and appears in this chat.",
        paused:
            "You clicked on the page, so the preview stopped on purpose: the AI never moves your screen while you work. It keeps working on your question and the answer appears in the chat here. Ask your next question to start the preview again.",
        docked: "Your Quaro AI chat. It stays here while you browse Quaro. Ask a question and the preview shows you where the data lives; the 'Preview' switch in the chat turns that off."
    }

    function wait(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms))
    }

    // "Follow the AI": idle -> armed (Lauf gestartet, Chat noch gross) -> following (Chat in der Leiste, Cursor fuehrt) -> docked (Antwort da, Chat bleibt
    // in der Leiste, man chattet dort weiter). Klickt oder tippt der Nutzer selbst auf der Seite, gehoert sie ihm: paused - die Vorschau endet bewusst
    // (das sagen Chip und Tooltip), der Chat laeuft weiter. Die Schritte kommen ueber $root von SEOKI.vue, das im KeepAlive weiterlaeuft.
    export default {
        name: "AiFollowBar",
        components: { AiCursor, AiFollowPanel },
        data: () => ({
            mode: "idle",
            label: "",
            next: null,
            pumping: false,
            lastNavAt: 0,
            busy: false,
            finishing: false,
            panelOpen: false,
            unread: false,
            panelLeft: 16
        }),
        computed: {
            visible() {
                return this.mode === "following" || this.mode === "paused" || this.mode === "docked"
            },
            working() {
                return (this.busy || this.finishing) && this.mode !== "paused"
            },
            chipIcon() {
                if (this.mode === "paused") return "mdi-hand-back-left-outline"
                return this.unread ? "mdi-message-badge-outline" : "mdi-robot-outline"
            },
            chipText() {
                if (this.mode === "paused") return this.busy ? "Preview stopped - you clicked" : "Preview stopped - answer ready"
                if (this.finishing && !this.busy) return "Answer ready - showing where it found the data"
                if (this.busy) return "Quaro AI: " + (this.label || "working")
                return this.unread ? "Answer ready" : "Quaro AI chat"
            },
            tooltipText() {
                if (this.mode === "paused") return TOOLTIPS.paused
                return this.busy || this.finishing ? TOOLTIPS.following : TOOLTIPS.docked
            },
            panelStyle() {
                return { left: this.panelLeft + "px" }
            }
        },
        watch: {
            $route(to) {
                // Wer selbst zurueck in den grossen Chat geht, braucht die Leiste nicht mehr.
                if (to.path === CHAT_PATH && (this.mode === "docked" || this.mode === "paused") && !this.busy) this.reset()
            },
            mode(value) {
                this.removeInputListeners()
                if (value === "armed" || value === "following") {
                    document.addEventListener("pointerdown", this.onUserInput, true)
                    document.addEventListener("keydown", this.onUserInput, true)
                    window.addEventListener("popstate", this.pause)
                }
            },
            panelOpen(open) {
                if (open) {
                    this.unread = false
                    this.$nextTick(this.placePanel)
                }
            }
        },
        methods: {
            content() {
                return document.querySelector("#content")
            },
            removeInputListeners() {
                document.removeEventListener("pointerdown", this.onUserInput, true)
                document.removeEventListener("keydown", this.onUserInput, true)
                window.removeEventListener("popstate", this.pause)
            },
            // Eigene Eingabe auf der Seite beendet die Vorschau. Die Leiste, das Panel und die Tooltips gehoeren zum Chat und zaehlen nicht.
            onUserInput(event) {
                const target = event.target
                if (!target || !target.closest) return
                const own = [this.$refs.root, this.$refs.panel, this.$refs.chip]
                if (own.some((el) => el && el.contains(target)) || target.closest(".v-tooltip__content, .menuable__content__active, #hamburg")) return
                this.pause()
            },
            pause() {
                if (this.mode !== "armed" && this.mode !== "following") return
                // Noch im grossen Chat (der Nutzer tippt oder klickt dort, der Cursor hat nichts bewegt): nichts zu pausieren.
                if (this.mode === "armed" || this.$route.path === CHAT_PATH) return this.reset()
                this.mode = "paused"
                this.next = null
                this.finishing = false
                // Die Antwort war schon fertig: sie wartet im Chat, ohne den Nutzer zu unterbrechen.
                if (!this.busy) this.unread = true
                this.$refs.cursor.hide()
            },
            reset() {
                this.next = null
                this.label = ""
                this.busy = false
                this.finishing = false
                this.panelOpen = false
                this.unread = false
                this.$refs.cursor.hide()
                this.mode = "idle"
            },
            dismiss() {
                this.reset()
            },
            togglePanel() {
                this.panelOpen = !this.panelOpen
            },
            placePanel() {
                const chip = this.$refs.chip
                if (!chip) return
                const rect = chip.getBoundingClientRect()
                const width = 480
                this.panelLeft = Math.max(12, Math.min(Math.round(rect.left), window.innerWidth - width - 16))
            },
            onRun({ running }) {
                if (running) {
                    this.busy = true
                    this.finishing = false
                    this.label = ""
                    this.panelOpen = false
                    const wanted = followWanted() && !this.$vuetify.breakpoint.smAndDown
                    if (this.mode === "docked" || this.mode === "paused") {
                        // Der Chat steckt schon in der Leiste: eine neue Frage startet die Vorschau dort neu, wenn sie an ist.
                        this.$refs.cursor.hide()
                        this.mode = wanted ? "following" : "docked"
                    } else if (this.mode === "idle" && wanted && this.$route.path === CHAT_PATH) {
                        this.mode = "armed"
                    }
                    return
                }
                this.busy = false
                if (this.mode === "following") {
                    // Die Antwort ist da, der Weg zur Kachel aber vielleicht noch nicht zu Ende: er laeuft aus, dann klappt der Chat auf.
                    this.finishPreview()
                } else if (this.mode === "paused") {
                    // Der Nutzer hat selbst geklickt: die Antwort wartet im Chat, ohne ihn zu unterbrechen.
                    this.mode = "docked"
                    this.unread = true
                } else if (this.mode === "docked") {
                    this.panelOpen = true
                } else if (this.mode === "armed") {
                    this.reset()
                }
            },
            async finishPreview() {
                this.finishing = true
                const start = Date.now()
                while ((this.pumping || this.next) && this.mode === "following" && !this.busy && Date.now() - start < 25000) await wait(200)
                if (!this.finishing || this.busy || this.mode !== "following") return
                this.finishing = false
                this.mode = "docked"
                this.next = null
                this.$refs.cursor.hide()
                this.panelOpen = true
            },
            onStep(step) {
                if (this.mode !== "armed" && this.mode !== "following") return
                // Sicherheitsnetz: den Chat ohne Eingabe verlassen (z. B. Sprung aus dem Code) - dann fuehrt der Cursor nicht mehr.
                if (this.mode === "armed" && this.$route.path !== CHAT_PATH) return this.reset()
                this.label = followStepLabel(step)
                const target = resolveFollowTarget(step)
                if (!target) return
                // Eine Listen-Abfrage (z. B. Rankings ueber alle Projekte) holt den Cursor nicht aus dem Projekt zurueck, in dem er gerade zeigt.
                const f = target.follow
                if (f && !f.project && f.list && this.$route.path.indexOf(f.list + "/") === 0) return
                this.next = { target, label: this.label }
                if (this.mode === "armed") this.minimizeChat()
                else this.pump()
            },
            // Der Chat geht in die Leiste; er laeuft im KeepAlive weiter.
            async minimizeChat() {
                this.mode = "following"
                await this.$nextTick()
                await this.$refs.cursor.flyGhost(this.content(), this.$refs.chip)
                this.pump()
            },
            needsNavigation(target) {
                const f = target.follow || { project: target.route }
                const dest = f.project || f.list
                return Boolean(dest) && this.$route.path !== dest.split("?")[0]
            },
            async pump() {
                if (this.pumping) return
                this.pumping = true
                while (this.mode === "following" && this.next) {
                    // Seitenwechsel mit Abstand; Reiter und Kacheln derselben Seite nicht ausbremsen.
                    const remaining = this.needsNavigation(this.next.target) ? this.lastNavAt + MIN_DWELL_MS - Date.now() : 0
                    if (remaining > 0) await wait(remaining)
                    if (this.mode !== "following" || !this.next) break
                    const item = this.next
                    this.next = null
                    await this.play(item)
                }
                this.pumping = false
            },
            async waitFor(selector, timeoutMs) {
                const start = Date.now()
                while (Date.now() - start < (timeoutMs || ELEMENT_WAIT_MS)) {
                    if (this.mode !== "following") return null
                    const el = document.querySelector(selector)
                    if (el && el.getBoundingClientRect().width) return el
                    await wait(100)
                }
                return null
            },
            async scrollToEl(el) {
                if (!el) return
                el.scrollIntoView({ behavior: "smooth", block: "center" })
                await wait(650)
            },
            async clickTo(el, label) {
                const cursor = this.$refs.cursor
                await cursor.moveTo(el, label)
                await cursor.pulse()
            },
            // Der Weg wie ein Mensch: Menue -> Projektzeile in der Liste -> Reiter -> Kachel. Findet der Cursor ein Element nicht, endet der Weg dort
            // (kein Leuchten ueber die ganze Seite, kein Fehler); die Seite selbst ist dann trotzdem offen.
            async play({ target, label }) {
                const f = target.follow || { project: target.route }
                if (f.project && this.$route.path !== f.project) {
                    if (f.rowId) {
                        if (this.$route.path !== f.list) {
                            await this.clickTo(document.querySelector('a[href="' + target.navHref + '"]'), label)
                            if (this.mode !== "following") return
                            this.lastNavAt = Date.now()
                            await this.goTo(f.list)
                        }
                        const row = await this.waitFor('[data-follow-row="' + f.rowId + '"]')
                        if (this.mode !== "following") return
                        if (row) {
                            await this.scrollToEl(row)
                            await this.clickTo(row, label)
                            if (this.mode !== "following") return
                            row.click()
                            await wait(900)
                        }
                    } else {
                        await this.clickTo(document.querySelector('a[href="' + target.navHref + '"]'), label)
                        if (this.mode !== "following") return
                        this.lastNavAt = Date.now()
                    }
                    if (this.mode === "following" && this.$route.path !== f.project) await this.goTo(f.project)
                } else if (!f.project && f.list && this.$route.path !== f.list) {
                    await this.clickTo(document.querySelector('a[href="' + target.navHref + '"]'), label)
                    if (this.mode !== "following") return
                    this.lastNavAt = Date.now()
                    await this.goTo(f.list)
                }
                if (this.mode !== "following") return
                if (f.tab) {
                    const tab = await this.waitFor('[data-follow-tab="' + f.tab + '"]', PAGE_WAIT_MS)
                    if (this.mode !== "following" || !tab) return
                    await this.clickTo(tab, label)
                    if (this.mode !== "following") return
                    // Ein aktiver Reiter wird nur gezeigt, ein anderer wirklich angeklickt.
                    if (!tab.classList.contains("active-button") && !tab.classList.contains("active-button2")) {
                        tab.click()
                        await wait(600)
                    }
                }
                if (f.tile && this.mode === "following") {
                    const tile = await this.waitFor(f.tile === "search" ? "#searchRow" : '[data-follow-tile="' + f.tile + '"]', PAGE_WAIT_MS)
                    if (this.mode !== "following" || !tile) return
                    await this.scrollToEl(tile)
                    if (this.mode !== "following") return
                    await this.clickTo(tile, label)
                    if (this.mode === "following") this.$refs.cursor.showHighlight(tile)
                }
            },
            async goTo(path) {
                if (this.$route.path === path.split("?")[0]) return
                try {
                    await this.$router.push(path)
                } catch (error) {
                    // doppelte oder abgebrochene Navigation ist hier nur Darstellung
                }
                if (this.mode === "following") await wait(150)
            },
            // "Full chat": zurueck in die grosse Ansicht, die Leiste endet.
            openChat() {
                this.reset()
                if (this.$route.path !== CHAT_PATH) this.$router.push(CHAT_PATH).catch(() => {})
            },
            // Das Panel hat eine Frage abgeschickt (der Chat im Hintergrund, SEOKI.vue, sendet sie): die Leiste zeigt ab jetzt "arbeitet", das Panel klappt zu.
            onPanelSend() {
                this.busy = true
                this.label = ""
                this.panelOpen = false
            }
        },
        mounted() {
            this.$root.$on("aiFollowRun", this.onRun)
            this.$root.$on("aiFollowStep", this.onStep)
            this.$root.$on("aiFollowSend", this.onPanelSend)
            // Panel und Cursor im Anwendungs-Container: die App-Leiste hat ein transform, darin waere position:fixed relativ zur Leiste.
            const app = document.querySelector(".v-application") || document.body
            if (this.$refs.panel) app.appendChild(this.$refs.panel)
        },
        beforeDestroy() {
            this.$root.$off("aiFollowRun", this.onRun)
            this.$root.$off("aiFollowStep", this.onStep)
            this.$root.$off("aiFollowSend", this.onPanelSend)
            this.removeInputListeners()
            const panel = this.$refs.panel
            if (panel && panel.parentNode) panel.parentNode.removeChild(panel)
        }
    }
</script>

<style scoped>
    .ai-follow-root {
        display: flex;
        align-items: center;
        margin-left: 12px;
    }
    .ai-follow-bar {
        display: flex;
        align-items: center;
        max-width: 420px;
        padding: 2px 6px 2px 10px;
        border: 1px solid rgba(0, 159, 137, 0.4);
        border-radius: 16px;
        background: rgba(0, 159, 137, 0.08);
        font-size: 13px;
    }
    .ai-follow-bar--paused {
        border-color: rgba(245, 124, 0, 0.5);
        background: rgba(245, 124, 0, 0.08);
    }
    .ai-follow-text {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    .ai-follow-dot {
        flex: 0 0 auto;
        width: 8px;
        height: 8px;
        margin-left: 6px;
        border-radius: 50%;
    }
    .ai-follow-panel-wrap {
        position: fixed;
        top: 68px;
        z-index: 250;
        width: 480px;
        max-width: calc(100vw - 24px);
    }
</style>
